import type { FeatureContent } from './types';

export const upskillingRecommendations: FeatureContent = {
  slug: 'upskilling-recommendations',
  path: '/features/upskilling-recommendations',
  audience: 'candidate',
  icon: 'GraduationCap',
  name: 'Upskilling Recommendations',
  navLabel: 'Upskilling Recommendations',
  navDescription: 'Know the gap. Get the plan to close it.',
  primaryKeyword: 'skill gap analysis',
  seo: {
    title: 'Skill Gap Analysis & Upskilling Plan | Talenture.ai',
    description:
      'Free skill gap analysis that maps your skills against real market demand and turns the gaps into a prioritised learning plan. Quick wins and gaps flagged.',
    keywords: [
      'skill gap analysis',
      'upskilling recommendations',
      'skills gap tool',
      'learning recommendations',
      'career development plan',
      'market readiness',
    ],
    ogTitle: 'Know the Gap. Get the Plan to Close It | Talenture.ai',
    ogDescription:
      'A skill gap analysis that maps you against real market demand and turns gaps into a prioritised plan, with quick wins and critical gaps flagged.',
  },
  hero: {
    eyebrow: 'For candidates',
    h1: 'Know the gap. Get the plan to close it',
    subhead:
      'It is not enough to be told you are missing a skill. Talenture maps your skills against live market demand and turns the gaps into a clear, ranked plan, so you always know what to learn next.',
    primaryCta: { label: 'Check your CV free', href: '/cv-vs-market' },
    secondaryCta: { label: 'Create free account', href: '/candidate-auth' },
  },
  definition: {
    heading: 'What is a skill gap analysis?',
    body: 'A skill gap analysis compares the skills you have against the skills a role or the market demands, then shows what is missing. Talenture goes further and ranks each gap by importance and effort, flags quick wins, and turns the result into a prioritised learning plan, so you know exactly where to focus.',
  },
  sections: [
    {
      icon: 'Gauge',
      title: 'Gaps ranked by effort',
      body: 'Not every gap is worth the same effort. Talenture labels each missing skill by how much work it takes to close, so you can plan realistically.',
      bullets: [
        'Quick to learn',
        'Moderate',
        'Extensive investment',
      ],
    },
    {
      icon: 'Zap',
      title: 'Quick wins, called out',
      body: 'Some gaps are high impact and easy to close. Talenture highlights these quick wins first, so you can lift your match fast before tackling the bigger ones.',
    },
    {
      icon: 'ListOrdered',
      title: 'Prioritised by what matters',
      body: 'Each gap is ranked by how much it affects your fit, so you spend your time on the skills that move the needle, not the ones that look impressive but change little.',
      bullets: [
        'Critical',
        'Important',
        'Beneficial',
      ],
    },
    {
      icon: 'LineChart',
      title: 'Where the market is going',
      body: 'Beyond a single role, Talenture gives you a market readiness score, a three-year view of your role, and the AI-resilient skills worth building toward, so you plan for the career, not just the next application.',
    },
  ],
  howTo: {
    heading: 'How the upskilling plan works',
    intro: 'Three steps from a skill gap to a prioritised learning plan.',
    steps: [
      {
        title: 'Map your skills against demand',
        body: 'Talenture compares your current skills with the requirements of a role and the direction of the wider market.',
      },
      {
        title: 'Rank each gap by impact and effort',
        body: 'Missing skills are prioritised by how much they affect your fit and labelled as quick to learn, moderate, or an extensive investment.',
      },
      {
        title: 'Follow the plan and look ahead',
        body: 'Quick wins and critical gaps become a clear learning plan, alongside a market readiness score and three-year view of the skills worth building.',
      },
    ],
  },
  proofPoints: [
    { value: '3 levels', label: 'of effort on every gap' },
    { value: '3-year', label: 'role and skills outlook' },
    { value: 'Always free', label: 'for candidates' },
  ],
  faq: [
    {
      question: 'What does the skill gap analysis show me?',
      answer:
        'It compares your skills against a role or the wider market and lists what is missing. Each gap is labelled by effort, ranked by importance, and high-impact quick wins are flagged, so you get a prioritised plan rather than a flat list.',
    },
    {
      question: 'How is this different from a generic course list?',
      answer:
        'A course list ignores your starting point. Talenture works from your actual skills and the real gaps Match Insights found, then ranks them by importance and effort, so the plan is specific to you and ordered by impact.',
    },
    {
      question: 'What is the market readiness score?',
      answer:
        'It is a view of how prepared you are for the current market, paired with a three-year outlook on your role and the AI-resilient skills worth building. It helps you plan beyond the next application toward where your field is heading.',
    },
    {
      question: 'Does it cost anything?',
      answer:
        'No. Talenture is always free for candidates, with no credit card required. The skill gap analysis and upskilling plan are part of the free career tools.',
    },
  ],
  relatedSlugs: ['match-insights', 'salary-negotiation', 'application-tracking'],
};
